/**
 * <ErrorBoundary> — catches runtime crashes in any descendant React component
 * and renders a friendly fallback UI instead of a blank screen.
 *
 * Why this exists:
 *   - A single uncaught error (TDZ, missing import, null-access) unmounts the
 *     entire React tree → user sees a white page → owner panics.
 *   - With this boundary, the rest of the app keeps working and the user sees
 *     a clear message + a "Reload" + "Report" button.
 *
 * Why class component:
 *   - getDerivedStateFromError + componentDidCatch are only available on class
 *     components. React still does not have a hook equivalent.
 */
import React from 'react';
import { AlertTriangle, RefreshCw, MessageCircle } from 'lucide-react';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });

    // Log to console — always useful in DevTools
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] React tree crashed', { error, errorInfo });

    // Fire Mixpanel + Sentry events so we see crashes in production without
    // waiting for a user to report them. Best-effort — never let analytics
    // crash the fallback UI itself.
    try {
      // eslint-disable-next-line global-require
      const { track } = require('../services/analytics');
      track?.('app_crash', {
        message: error?.message || String(error),
        stack: (error?.stack || '').slice(0, 1000),
        component_stack: (errorInfo?.componentStack || '').slice(0, 1000),
        path: typeof window !== 'undefined' ? window.location.pathname : '',
      });
    } catch {
      /* analytics best-effort */
    }
    try {
      // eslint-disable-next-line global-require
      const { captureException } = require('../sentry');
      captureException?.(error, {
        tags: {
          where: 'react:error_boundary',
          route: typeof window !== 'undefined' ? window.location.pathname : '',
        },
        extra: { component_stack: errorInfo?.componentStack },
      });
    } catch {
      /* sentry best-effort */
    }
  }

  handleReload = () => {
    if (typeof window !== 'undefined') window.location.reload();
  };

  handleGoHome = () => {
    if (typeof window !== 'undefined') {
      window.location.href = '/';
    }
  };

  render() {
    if (!this.state.error) {
      return this.props.children;
    }

    const message = this.state.error?.message || 'Unknown error';
    const isDev = process.env.NODE_ENV !== 'production';

    return (
      <div
        data-testid="error-boundary-fallback"
        className="min-h-screen w-full flex items-center justify-center bg-gradient-to-b from-red-50 to-white p-6"
      >
        <div className="max-w-lg w-full bg-white rounded-2xl shadow-xl ring-1 ring-red-100 p-8 text-center">
          <div className="w-14 h-14 mx-auto rounded-full bg-red-50 ring-1 ring-red-200 flex items-center justify-center mb-4">
            <AlertTriangle className="text-red-600" size={26} />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Something broke on this page
          </h1>
          <p className="text-gray-600 mb-6 leading-relaxed">
            We hit an unexpected error. Your data is safe — reloading usually fixes it.
            The error has been logged automatically.
          </p>

          {/* Error detail — visible in dev only so production users don't see internals */}
          {isDev && (
            <div className="mb-6 text-left bg-gray-50 ring-1 ring-gray-200 rounded-lg p-3 max-h-32 overflow-auto">
              <p className="text-xs font-mono text-red-700 break-words whitespace-pre-wrap">
                {message}
              </p>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              type="button"
              onClick={this.handleReload}
              data-testid="error-boundary-reload"
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm transition-colors"
            >
              <RefreshCw size={16} /> Reload page
            </button>
            <button
              type="button"
              onClick={this.handleGoHome}
              data-testid="error-boundary-home"
              className="inline-flex items-center justify-center px-5 py-2.5 rounded-lg bg-white ring-1 ring-gray-300 hover:ring-gray-400 text-gray-800 font-semibold text-sm transition-colors"
            >
              Go to homepage
            </button>
          </div>

          <a
            href="https://wa.me/919821922296?text=Hi%2C%20I%20hit%20an%20error%20on%20RentKollect"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="error-boundary-support"
            className="mt-5 inline-flex items-center justify-center gap-1.5 text-xs text-gray-500 hover:text-emerald-700 transition-colors"
          >
            <MessageCircle size={12} /> Report on WhatsApp
          </a>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
